import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import clsx from 'clsx';
import _ from 'lodash';

import { PendingChangeKind, PendingChangesActionType } from 'constants/pendingChanges';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { getModifiedFieldChangeKind } from 'utils/pendingChangeKind';

import InfoTitle from '../../coreDesignSystem/InfoTitle';
import SearchInputCDS from '../../coreDesignSystem/SearchInputCDS';
import TooltipedSortLabelCDS from '../../coreDesignSystem/TooltipedSortLabelCDS';
import AddRemoveIcon from '../AddRemoveIcon';
import PendingChangeWrapper from '../PendingChangeWrapper';
import { SORT_LABEL_ALIGNMENT } from '../TooltipedSortLabel';

import {
  DEFAULT_SKELETONS_ROWS,
  DEFAULT_VALID_QUERY_LENGTH,
  DEFAULT_VISIBLE_PREVIEW_ROWS,
  IS_NEAR_BOTTOM,
  simpleTablePropTypes,
} from './SimpleTable.constants';
import {
  addButtonStyles,
  dividerStyles,
  flexCellStyles,
  getBodyCellStyles,
  getHeadCellStyles,
  getWrapperStyles,
  headerStyles,
  pendingChangesContainerStyles,
  rowStyles,
  searchControlsWrapperStyles,
  tableContainerStyles,
  tableStyles,
  titleContainerStyles,
  titleWrapperStyles,
  typographyStyles,
} from './SimpleTable.styles';
import { getCellFieldNames, getRowData, getSearchAriaLabel } from './SimpleTable.utils';
import SimpleTableSkeleton from './SimpleTableSkeleton';
import SkeletonCell from './SkeletonCell';

const renderCells = ({
  row,
  rowIndex,
  configEntries,
  data,
  isCustomDataDefinition,
  withInternalBorders,
  pendingChangesTextSelector,
  isAdditionalRowContentVisible,
  isDragging,
  getCellPendingChangeAuthors,
}) =>
  configEntries.map(
    (
      [
        key,
        {
          content,
          align,
          rowSpan,
          colSpan,
          customStyles,
          fieldName,
          renderContentAsText,
          width,
          minWidth,
          label,
        },
      ],
      cellIndex,
    ) => {
      const currentCellPosition = cellIndex + 1;

      const cellFieldNames = getCellFieldNames({ key, fieldName });
      const cellChangeKind =
        row.pendingChangesActionType === PendingChangesActionType.MODIFIED
          ? getModifiedFieldChangeKind(row, cellFieldNames)
          : null;
      const isModified = cellChangeKind === PendingChangeKind.DIRECT;
      const cellPendingChangeAuthors =
        isModified && getCellPendingChangeAuthors
          ? getCellPendingChangeAuthors(row, cellFieldNames)
          : null;

      const shouldBeRenderedX =
        !isCustomDataDefinition ||
        Object.values(row).every(
          cell =>
            !(
              currentCellPosition > cell.position &&
              cell.position + (cell.colSpan - 1) >= currentCellPosition
            ),
        );

      const rowSpanStartIndex = data
        .slice(0, rowIndex)
        .findLastIndex(r => r[key] && r[key]?.rowSpan);

      const shouldBeRenderedY =
        !isCustomDataDefinition ||
        rowSpanStartIndex === -1 ||
        rowSpanStartIndex === rowIndex ||
        rowIndex < rowSpanStartIndex ||
        rowIndex > rowSpanStartIndex + data[rowSpanStartIndex][key].rowSpan - 1;

      if (!shouldBeRenderedX || !shouldBeRenderedY) {
        return null;
      }

      const renderedContent = !!content && content(row);
      const shouldBeRenderedAsText = !!content && !!renderContentAsText && renderContentAsText(row);

      return (
        <TableCell
          align={align}
          className="table-cell"
          colSpan={colSpan ? colSpan(row) : undefined}
          data-cell-modified={isModified}
          data-label={label}
          key={key}
          rowSpan={rowSpan ? rowSpan(row) : undefined}
          sx={getBodyCellStyles({
            withInternalBorders,
            customStyles: customStyles ? customStyles(row) : null,
            isModified,
            width: isDragging ? width : undefined,
            minWidth,
          })}
        >
          <PendingChangeWrapper
            authors={cellPendingChangeAuthors}
            changeKind={cellChangeKind}
            className="table-cell__content_box"
            fieldLabel={_.isString(label) ? label : ''}
            sx={flexCellStyles({
              align,
              isModified,
              isAdditionalRowContentVisible,
            })}
            textSelector={pendingChangesTextSelector}
          >
            {content && !shouldBeRenderedAsText ? (
              renderedContent
            ) : (
              <Typography
                data-truncated
                className="table-cell__content_text"
                sx={typographyStyles}
                textAlign={align}
                variant="regular14"
              >
                {content && shouldBeRenderedAsText ? renderedContent : row[key]}
              </Typography>
            )}
          </PendingChangeWrapper>
        </TableCell>
      );
    },
  );

const SimpleTable = ({
  title = '',
  config,
  data,
  isLoading,
  onAddClick,
  withDivider = false,
  withSearch,
  withAddButton = false,
  withInternalBorders = false,
  customStyles,
  isPreview = false,
  previewRows = DEFAULT_VISIBLE_PREVIEW_ROWS,
  skeletonRows = DEFAULT_SKELETONS_ROWS,
  isCustomDataDefinition = false,
  isHideTableWhenEmpty = false,
  onRowClick,
  onSearch,
  columnFilters,
  onColumnFiltersChange,
  additionalRowContentRowId,
  additionalRowContent,
  order,
  onRequestSort,
  isSortDisabled = false,
  pendingChangesCount,
  pendingChangesTextSelector = 'p',
  className,
  withNoAvailableMessage,
  isStickyHeader = false,
  isAddButtonDisabled,
  isSearchOnChange = false,
  validQueryLength = DEFAULT_VALID_QUERY_LENGTH,
  customSearchControls,
  fetchTableData,
  withSelectableRows = false,
  noAvailableMessageText = 'No results',
  noAvailableMessageIcon,
  rowTestId,
  getRowProps,
  containerRef,
  searchDataTestId,
  searchAriaLabel,
  // Droppable props
  isDroppable,
  onDragEnd,
  droppableId,
  tooltipEnterDelay,
  dataTestId,
  getCellPendingChangeAuthors = null,
}) => {
  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral({
    enterDelay: tooltipEnterDelay,
  });
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const ownContainerRef = useRef(null);
  const tableContainerRef = containerRef ?? ownContainerRef;

  useEffect(() => {
    if (!fetchTableData) {
      return undefined;
    }

    const container = tableContainerRef.current;

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - IS_NEAR_BOTTOM;

      if (isNearBottom) {
        fetchTableData();
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => container.removeEventListener('scroll', handleScroll);
  }, [fetchTableData, tableContainerRef]);

  const configEntries = Object.entries(config).filter(([, { isHidden }]) => !isHidden);

  const searchAriaLabelText = getSearchAriaLabel(searchAriaLabel, title);

  const isEmpty = !data?.length;
  const isHover = !!onRowClick || !!getRowProps;
  const columnsCount = configEntries.length;

  const changesCount = useMemo(
    () =>
      pendingChangesCount ??
      data?.reduce((acc, { pendingChangesActionType, modifiedFields = [], isAdded, isDeleted }) => {
        const currentItemChangesCount =
          pendingChangesActionType === PendingChangesActionType.ADDED ||
          isAdded ||
          pendingChangesActionType === PendingChangesActionType.DELETED ||
          isDeleted
            ? 1
            : modifiedFields.length;

        acc += currentItemChangesCount;

        return acc;
      }, 0),
    [data, pendingChangesCount],
  );

  const slicedData = useMemo(
    () => data?.slice(0, isPreview ? previewRows : data.length) ?? [],
    [data, isPreview, previewRows],
  );

  const visibleData = useMemo(
    () =>
      slicedData.map((row, rowIndex) => ({
        row,
        rowIndex,
        ...getRowData({
          row,
          rowIndex,
          isCustomDataDefinition,
          additionalRowContentRowId,
          additionalRowContent,
        }),
      })),
    [slicedData, isCustomDataDefinition, additionalRowContentRowId, additionalRowContent],
  );

  const shouldShowChangesCounter = changesCount > 0;

  const handleRowClick = useCallback(
    e => {
      if (!onRowClick) {
        return;
      }

      const { rowKey, rowId } = e.currentTarget.dataset;

      if (withSelectableRows) {
        setSelectedRowKey(rowKey);
      }

      onRowClick(rowId);
    },
    [onRowClick, withSelectableRows],
  );

  return (
    <Box
      className={clsx('wrapper', className)}
      sx={getWrapperStyles({ customStyles })}
      onMouseLeave={onResetTooltipState}
      onMouseOver={onMouseOver}
    >
      <TruncatedProvider />
      {(title || shouldShowChangesCounter || withAddButton || withDivider || withSearch) && (
        <>
          {(title || shouldShowChangesCounter || withAddButton) && (
            <Box className="head-wrapper" sx={headerStyles}>
              {(title || changesCount > 0) && (
                <Box sx={titleWrapperStyles}>
                  {isLoading ? (
                    <Skeleton height={21} variant="rounded" width="calc(80% - 40px)" />
                  ) : (
                    <Box className="titleContainer" sx={titleContainerStyles}>
                      {title && <InfoTitle title={title} />}
                      {shouldShowChangesCounter && (
                        <PendingChangeWrapper
                          className="pending-changes-container"
                          pendingChangesActionType={PendingChangesActionType.MODIFIED}
                          sx={pendingChangesContainerStyles}
                        >
                          <Typography className="pending-changes-text" variant="regular14">
                            {changesCount} {changesCount > 1 ? 'Pending Changes' : 'Pending Change'}
                          </Typography>
                        </PendingChangeWrapper>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {withAddButton && (
                <>
                  {isLoading ? (
                    <Skeleton height={20} variant="rounded" width={20} />
                  ) : (
                    <IconButton
                      disableRipple
                      className="add-button"
                      data-testid={dataTestId}
                      disabled={isAddButtonDisabled}
                      sx={addButtonStyles}
                      onClick={onAddClick}
                    >
                      <AddRemoveIcon />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          )}

          {withDivider && <Divider className="divider" sx={dividerStyles} />}

          {withSearch && (
            <Box sx={searchControlsWrapperStyles}>
              <SearchInputCDS
                focusOnType
                ariaLabel={searchAriaLabelText}
                dataTestId={searchDataTestId}
                disabled={isLoading}
                searchOnType={isSearchOnChange}
                validQueryLength={validQueryLength}
                width="50%"
                onSearch={onSearch}
              />
              {customSearchControls}
            </Box>
          )}
        </>
      )}

      {!(isHideTableWhenEmpty && isEmpty) && (
        <TableContainer
          className="table-container"
          ref={tableContainerRef}
          sx={tableContainerStyles}
        >
          <Table className="table" stickyHeader={isStickyHeader} sx={tableStyles}>
            <TableHead className="table-head">
              <TableRow className="table-row">
                {isLoading ? (
                  <SkeletonCell />
                ) : (
                  configEntries.map(
                    ([
                      key,
                      {
                        sortBy,
                        width,
                        minWidth,
                        label,
                        align,
                        customStyles,
                        sort,
                        tooltip,
                        tooltipIcon,
                        filter,
                      },
                    ]) => {
                      const hasSortOrFilter = sort || tooltip || filter;
                      const sortLabel = (
                        <TooltipedSortLabelCDS
                          align={SORT_LABEL_ALIGNMENT[_.upperCase(align)]}
                          columnFilters={columnFilters}
                          columnKey={key}
                          disabled={isSortDisabled}
                          filter={filter}
                          id={sortBy ?? key}
                          isSort={sort}
                          label={label}
                          order={order}
                          tooltip={tooltip}
                          tooltipIcon={tooltipIcon}
                          onColumnFiltersChange={onColumnFiltersChange}
                          onRequestSort={onRequestSort}
                        />
                      );

                      return (
                        <TableCell
                          data-truncated
                          align={align}
                          className="table-cell"
                          key={key}
                          sortDirection={order?.id === key ? order.direction : false}
                          sx={getHeadCellStyles({
                            width,
                            minWidth,
                            withInternalBorders,
                            customStyles,
                          })}
                        >
                          {hasSortOrFilter ? sortLabel : label}
                        </TableCell>
                      );
                    },
                  )
                )}
              </TableRow>
            </TableHead>

            {isDroppable ? (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable direction="vertical" droppableId={droppableId || 'droppable-table'}>
                  {provided => (
                    <TableBody
                      {...provided.droppableProps}
                      className="table-body"
                      ref={provided.innerRef}
                    >
                      {isLoading ? (
                        <SimpleTableSkeleton rowsAmount={skeletonRows} />
                      ) : (
                        visibleData.map(
                          ({
                            row,
                            rowIndex,
                            rowKey,
                            rowId,
                            draggableId,
                            isAdditionalRowContentVisible,
                            isDeleted,
                            isAdded,
                            isDisabled,
                            isDragDisabled,
                            rowPendingChangesActionType,
                          }) => (
                            <Draggable
                              draggableId={draggableId}
                              index={rowIndex}
                              isDragDisabled={isDragDisabled}
                              key={draggableId}
                            >
                              {(draggableProvided, draggableSnapshot) => (
                                <PendingChangeWrapper
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  className="table-row"
                                  component={TableRow}
                                  {...getRowProps?.(row)}
                                  data-rfd-dragging={draggableSnapshot.isDragging}
                                  data-row-added={isAdded}
                                  data-row-deleted={isDeleted}
                                  data-row-disabled={isDisabled}
                                  data-row-drag-disabled={isDragDisabled}
                                  data-row-id={rowId}
                                  data-row-key={rowKey}
                                  data-row-with-additional-content={isAdditionalRowContentVisible}
                                  data-testid={rowTestId}
                                  hover={isHover && !isDisabled}
                                  pendingChangesActionType={
                                    rowPendingChangesActionType || undefined
                                  }
                                  selected={withSelectableRows && selectedRowKey === rowKey}
                                  sx={rowStyles({ isHover, isAdditionalRowContentVisible })}
                                  textSelector={pendingChangesTextSelector}
                                  onClick={isDisabled ? null : handleRowClick}
                                >
                                  {renderCells({
                                    row,
                                    rowIndex,
                                    configEntries,
                                    data: slicedData,
                                    isCustomDataDefinition,
                                    withInternalBorders,
                                    pendingChangesTextSelector,
                                    isAdditionalRowContentVisible,
                                    isDragging: draggableSnapshot.isDragging,
                                    getCellPendingChangeAuthors,
                                  })}
                                </PendingChangeWrapper>
                              )}
                            </Draggable>
                          ),
                        )
                      )}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <TableBody className="table-body">
                {isLoading ? (
                  <SimpleTableSkeleton rowsAmount={skeletonRows} />
                ) : (
                  visibleData.map(
                    ({
                      row,
                      rowIndex,
                      rowKey,
                      rowId,
                      isAdditionalRowContentVisible,
                      isDeleted,
                      isAdded,
                      isDisabled,
                      rowPendingChangesActionType,
                    }) => (
                      <Fragment key={rowKey}>
                        <PendingChangeWrapper
                          className="table-row"
                          component={TableRow}
                          {...getRowProps?.(row)}
                          data-row-added={isAdded}
                          data-row-deleted={isDeleted}
                          data-row-disabled={isDisabled}
                          data-row-id={rowId}
                          data-row-key={rowKey}
                          data-row-with-additional-content={isAdditionalRowContentVisible}
                          data-testid={rowTestId}
                          hover={isHover && !isDisabled}
                          key={rowKey}
                          pendingChangesActionType={rowPendingChangesActionType || undefined}
                          selected={withSelectableRows && selectedRowKey === rowKey}
                          sx={rowStyles({ isHover, isAdditionalRowContentVisible })}
                          textSelector={pendingChangesTextSelector}
                          onClick={isDisabled ? undefined : handleRowClick}
                        >
                          {renderCells({
                            row,
                            rowIndex,
                            configEntries,
                            data: slicedData,
                            isCustomDataDefinition,
                            withInternalBorders,
                            pendingChangesTextSelector,
                            isAdditionalRowContentVisible,
                            getCellPendingChangeAuthors,
                          })}
                        </PendingChangeWrapper>
                        {isAdditionalRowContentVisible && (
                          <TableRow
                            className="additional-row-content"
                            data-is-changed={
                              !!rowPendingChangesActionType &&
                              rowPendingChangesActionType !== PendingChangesActionType.NO_CHANGES
                            }
                            key={`additional-${rowKey}-content`}
                          >
                            <TableCell
                              className="additional-row-content-cell"
                              colSpan={columnsCount}
                            >
                              <Collapse
                                unmountOnExit
                                className="additional-row-content-cell-collapse"
                                in={isAdditionalRowContentVisible}
                                timeout="auto"
                              >
                                {additionalRowContent}
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ),
                  )
                )}
              </TableBody>
            )}
          </Table>
          {isEmpty && !isLoading && withNoAvailableMessage && (
            <NoAvailableMessage
              Icon={noAvailableMessageIcon}
              className="empty-message-row"
              message={noAvailableMessageText}
            />
          )}
        </TableContainer>
      )}
    </Box>
  );
};

SimpleTable.propTypes = simpleTablePropTypes;

export default memo(SimpleTable);
